import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VisualPanel from './VisualPanel';
import { LoginPanel } from './LoginPanel';
import axios from 'axios';
import Loader from "../../component/Loader/Loader";
import './login.css';

const OvcNewLogin = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [clientUid, setClientUid] = useState("");
    const [error, setError] = useState("");
    const [isloader, setIsLoader] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    const hydrateUserContext = async (token) => {
        setIsLoader(true);
        setError("");
        try {
            const authUrl = process.env.REACT_APP_FINBASE_AUTH_URL;
            const response = await axios.get(`${authUrl}/api/v1/iam/me/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const meData = response.data;
            setIsLoader(false);

            const role = meData.role || (meData.roles && meData.roles[0]) || 'USER';
            const userEmail = meData.email;

            const ovcUser = {
                id: meData.user_id,
                email: userEmail,
                name: userEmail ? userEmail.split('@')[0] : 'User',
                role: role,
                cognito_groups: meData.roles ? meData.roles.join(',') : '',
            };

            localStorage.setItem("ovcData", JSON.stringify(ovcUser));
            localStorage.setItem("email", userEmail);
            localStorage.setItem("tenantId", meData.tenant_id || "");

            if (ovcUser.cognito_groups) {
                const cognito = ovcUser.cognito_groups + ", " + ovcUser.role;
                localStorage.setItem("multiplerole", cognito);
            }

            const permissions = meData.permissions_flat || [];
            // Map permissions to expected uppercase/underscore/hyphen formats for legacy OVS components
            const mappedPermissions = [];
            permissions.forEach(p => {
                mappedPermissions.push(p);

                const upperDotReplaced = p.toUpperCase().replace(/\./g, '_');
                mappedPermissions.push(upperDotReplaced);

                if (p.includes('.')) {
                    const actionPart = p.split('.').slice(1).join('_').toUpperCase();
                    mappedPermissions.push(actionPart);

                    if (actionPart.startsWith('OVC_CAMPAIGNS_DAILER')) {
                        mappedPermissions.push("OVC-CAMPAIGNS DAILER");
                    }
                    if (actionPart.includes('CUSTOMER_PROFILE_DIALER')) {
                        mappedPermissions.push("OVC-CUSTOMER PROFILE DIALER");
                        mappedPermissions.push("OVC- CUSTOMER PROFILE DIALER");
                    }
                    if (actionPart === 'SAHYOG_WORKFLOW') {
                        mappedPermissions.push("sahyog-workflow");
                    }
                    if (actionPart === 'INSURANCE_WORKFLOW') {
                        mappedPermissions.push("Insurance-workflow");
                    }
                    if (actionPart === 'BUSINESSPRIME_WORKFLOW') {
                        mappedPermissions.push("businessprime-workflow");
                    }
                    if (actionPart === 'BUSINESSPRIME_WORKFLOW_MAPPING') {
                        mappedPermissions.push("businessprime-workflow-mapping");
                    }
                    if (actionPart === 'SKIP_TRACING') {
                        mappedPermissions.push("Skip-Tracing");
                    }
                    if (actionPart.startsWith('CET_')) {
                        const cetHyphenated = actionPart.toLowerCase().replace(/_/g, '-');
                        mappedPermissions.push(cetHyphenated);
                    }
                    if (actionPart === 'COLLECTION_WORKFLOW') {
                        mappedPermissions.push("collection-workflow");
                    }
                }
            });
            const uniqueMappedPermissions = [...new Set(mappedPermissions)];
            localStorage.setItem("Permissions", JSON.stringify(uniqueMappedPermissions));

            navigate("/dashboard");
        } catch (error) {
            setIsLoader(false);
            console.error("Failed to fetch user context:", error);
            setError("Failed to fetch user context from Finbase Auth.");
            localStorage.clear();
        }
    };

    useEffect(() => {
        setIsMounted(true);

        // Capture access_token and refresh_token from URL query parameters (SSO)
        const params = new URLSearchParams(window.location.search);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (accessToken && refreshToken) {
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("auth_token", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
            localStorage.setItem("OVCLOGGEDIN", JSON.stringify(true));

            // Clean up tokens from URL history to prevent header/cookie too large
            const urlWithoutParams = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, urlWithoutParams);

            hydrateUserContext(accessToken);
        }
    }, [navigate]);

    const handleLogin = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setIsLoader(true);
        setError("");
        try {
            const authUrl = process.env.REACT_APP_FINBASE_AUTH_URL || 'https://finbase-api.staging.arth.bz';
            const payload = {
                email: email,
                password: password,
                client_uid: clientUid
            };
            const response = await axios.post(`${authUrl}/api/v1/login/`, payload);
            const data = response.data;

            if (data && data.access && data.refresh) {
                localStorage.setItem("accessToken", data.access);
                localStorage.setItem("auth_token", data.access);
                localStorage.setItem("refreshToken", data.refresh);
                localStorage.setItem("OVCLOGGEDIN", JSON.stringify(true));

                await hydrateUserContext(data.access);
            } else {
                setIsLoader(false);
                setError("Invalid credentials or login failed.");
            }
        } catch (error) {
            setIsLoader(false);
            const msg = error?.response?.data?.detail || error?.response?.data?.message || error?.response?.data?.error || "An error occurred. Please try again.";
            setError(msg);
        }
    };

    if (isloader) {
        return (
            <div className="loader-style">
                <Loader />
            </div>
        );
    }

    const loginPanelClasses = `login-panel ${isMounted ? 'is-mounted' : ''}`;
    const contentClasses = `login-panel__content ${isloader ? 'is-loading' : ''}`;

    return (
        <main className="login-page-main">
            <div className="login-container">
                <VisualPanel isMounted={isMounted} />

                <div className={loginPanelClasses}>
                    <div className={contentClasses}>
                        <LoginPanel
                            onLogin={handleLogin}
                            email={email}
                            setEmail={setEmail}
                            password={password}
                            setPassword={setPassword}
                            clientUid={clientUid}
                            setClientUid={setClientUid}
                            invalidCreds={error}
                        />
                    </div>
                </div>
            </div>
        </main>
    );
};

export default OvcNewLogin;